import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { City } from '@/data/cities';
import { CityLanguage } from '@/context/languages/languageData';
import { supabase } from '@/integrations/supabase/client';

interface SpecialtyTranslations {
  [key: string]: string;
}

interface UseCitySpecialtyTranslationsResult {
  translations: SpecialtyTranslations;
  isLoading: boolean;
  error: string | null;
  hasTranslation: boolean;
}

export const useCitySpecialtyTranslations = (
  city: City | null,
  specialtySlug: string | null,
  language: CityLanguage
): UseCitySpecialtyTranslationsResult => {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Generate safe default translations
  const defaultTranslations: SpecialtyTranslations = React.useMemo(() => {
    if (!city || !specialtySlug) return {};
    
    const specialtyName = specialtySlug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    
    return {
      title: `${specialtyName} Therapists in ${city.name}`,
      description: `Find ${specialtyName} specialists in ${city.name}`,
      heroTitle: `${specialtyName} in ${city.name}`,
      heroDescription: `Connect with certified ${specialtyName} professionals in ${city.name}`,
    };
  }, [city, specialtySlug]);

  // Fetch from Supabase with React Query
  const { data: dbTranslations, isLoading: isLoadingDb } = useQuery({
    queryKey: ['city-specialty-translations', city?.slug, specialtySlug, language],
    queryFn: async () => {
      if (!city || !specialtySlug) return null;

      try {
        const { data, error } = await supabase
          .from('city_translations')
          .select('data')
          .eq('city_slug', city.slug)
          .eq('specialty_slug', specialtySlug)
          .eq('language', language)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = not found
          console.warn('Specialty translation fetch failed:', error);
          return null;
        }

        return data?.data as SpecialtyTranslations || null;
      } catch (e) {
        console.error('Unexpected specialty translation error:', e);
        return null;
      }
    },
    enabled: !!city && !!specialtySlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Fallback logic with safe defaults
  const translations = (() => {
    if (!city || !specialtySlug) return defaultTranslations;

    // Use database translations if available
    if (dbTranslations) {
      return { ...defaultTranslations, ...dbTranslations };
    }

    // Check localStorage cache
    try {
      const cacheKey = `city_${city.slug}_${specialtySlug}_${language}`;
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const parsed = JSON.parse(cachedData) as SpecialtyTranslations;
        return { ...defaultTranslations, ...parsed };
      }
    } catch (e) {
      console.warn('Error parsing cached specialty translations:', e);
    }

    return defaultTranslations;
  })();

  // Subscribe to Realtime translation updates
  useEffect(() => {
    if (!city || !specialtySlug) return;

    const channel = supabase.channel('specialty-translation-updates')
      .on('broadcast', { event: 'translation_completed' }, (payload: any) => {
        const { city_slug, specialty_slug, language: translationLang, translations } = payload.payload;
        
        if (city_slug === city.slug && specialty_slug === specialtySlug && translationLang === language) {
          console.log('🔄 Realtime specialty translation update received for', city_slug, specialtySlug, language);
          
          // Update React Query cache immediately
          queryClient.setQueryData(
            ['city-specialty-translations', city.slug, specialtySlug, language],
            { data: translations[language] }
          );
          
          // Also cache to localStorage for persistence
          const cacheKey = `city_${city.slug}_${specialtySlug}_${language}`;
          localStorage.setItem(cacheKey, JSON.stringify(translations[language]));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [city, specialtySlug, language, queryClient]);

  return {
    translations,
    isLoading: isLoadingDb,
    error,
    hasTranslation: Object.keys(translations).length > 0
  };
};
